import { SelectedData } from 'owa-addins-types';
import IAddinEditorPlugin from 'owa-editor-addin-plugin-types/lib/PluginInterface/IAddinEditorPlugin';
import BodyType from 'owa-service/lib/contract/BodyType';
import { Editor } from 'roosterjs-editor-core';
import { ContentPosition, InsertOption, PositionType } from 'roosterjs-editor-types';
import { CoercionType } from 'owa-addins-core';
import removeAppendOnSend from './removeAppendOnSend';
import { logUsage } from 'owa-analytics';
import { AppendOnSendViewState } from 'owa-addins-core';
import { PluginWithViewState, PluginViewStateAdapter } from 'owa-editor';

export default class AddinBodyEditorPlugin
    implements IAddinEditorPlugin, PluginWithViewState<AppendOnSendViewState> {
    private editor: Editor;
    private plainTextEditor: HTMLTextAreaElement;

    constructor(public readonly adapter: PluginViewStateAdapter<AppendOnSendViewState>) {}

    public getName() {
        return 'AddIn';
    }

    public initialize(editor: Editor) {
        this.editor = editor;
    }

    public dispose() {
        this.editor = null;
        this.plainTextEditor = null;
    }

    private isPlainTextEditor(): boolean {
        return !!this.plainTextEditor;
    }

    public setPlainTextEditor(plainTextEditor: HTMLTextAreaElement) {
        this.plainTextEditor = plainTextEditor;
    }

    public updateContent(content: string, bodyType: BodyType) {
        if (this.editor.getContent(false /* will trigger ExtractContentEvent */) == content) {
            return;
        }
        this.editor.setContent('');
        let contentElement = this.createNodeFromContent(content);
        this.insertNode(contentElement, {
            position: ContentPosition.Begin,
            updateCursor: true,
            replaceSelection: false,
            insertOnNewLine: false,
        });
        this.editor.select(contentElement, PositionType.After);
        this.triggerContentChangedByAddinEvent();
        this.editor.addUndoSnapshot();
    }

    public getContent(): string {
        return this.isPlainTextEditor() ? this.plainTextEditor.value : this.editor.getContent();
    }

    public getSelectedData(): SelectedData {
        if (this.isPlainTextEditor()) {
            const start = this.plainTextEditor.selectionStart;
            const end = this.plainTextEditor.selectionEnd;
            const data = this.plainTextEditor.value.substring(start, end);
            return {
                data: data,
                sourceProperty: 'body',
                startPosition: start,
                endPosition: end,
            };
        } else {
            let currentActiveSelectionRange = this.editor.getSelectionRange();
            let clonedSelection = currentActiveSelectionRange.cloneContents();
            let div = document.createElement('div');
            div.appendChild(clonedSelection);
            return {
                data: div.innerHTML.length > 0 ? div.innerHTML : null,
                sourceProperty: 'body',
                startPosition: currentActiveSelectionRange.startOffset,
                endPosition: currentActiveSelectionRange.endOffset,
            };
        }
    }

    public prependContent(content: string) {
        if (this.isPlainTextEditor()) {
            this.plainTextEditor.value = content + this.plainTextEditor.value;
        } else {
            let contentElement = this.createNodeFromContent(content);
            this.insertNode(contentElement, {
                position: ContentPosition.Begin,
                updateCursor: false,
                replaceSelection: false,
                insertOnNewLine: false,
            });
        }
    }

    public setSelectedData(content: string) {
        if (this.isPlainTextEditor()) {
            const start = this.plainTextEditor.selectionStart;
            const end = this.plainTextEditor.selectionEnd;
            this.plainTextEditor.value =
                this.plainTextEditor.value.substring(0, start) +
                content +
                this.plainTextEditor.value.substring(end + 1);
        } else {
            let contentElement = this.createNodeFromContent(content);
            const contentPosition = this.editor.getSelectionRange()
                ? ContentPosition.SelectionStart
                : ContentPosition.End;
            this.insertNode(contentElement, {
                position: contentPosition,
                updateCursor: true,
                replaceSelection: true,
                insertOnNewLine: false,
            });
        }
    }

    public deleteContentWithSelector(selector: string) {
        this.editor.queryElements(selector, (node: HTMLElement) => {
            this.editor.deleteNode(node);
        });
    }

    private insertNode(contentElement: HTMLElement, options: InsertOption) {
        this.editor.addUndoSnapshot();
        this.editor.focus();
        this.triggerContentChangedByAddinEvent();
        this.editor.insertNode(contentElement, options);
        this.triggerContentChangedByAddinEvent();
        this.editor.addUndoSnapshot();
    }

    private createNodeFromContent(content: string): HTMLElement {
        let contentNode: HTMLElement = this.editor
            .getDocument()
            .createElement('span') as HTMLAnchorElement;

        /* The param 'content' is already sanitized before being passed to this method */
        // tslint:disable-next-line:no-inner-html
        contentNode.innerHTML = content;
        return contentNode;
    }

    public triggerContentChangedByAddinEvent() {
        this.editor.triggerContentChangedEvent('Addins');
    }

    public insertAppendOnSend(bodyType: BodyType = 'HTML') {
        const viewState = this.adapter();
        const appendOnSend = viewState.appendOnSend;
        let stringToAppend: string = '';
        const name = 'ComposeAppendOnSend';
        if (bodyType == 'HTML' && this.editor) {
            appendOnSend.map(value => {
                let textToAppend: string = value.txt;
                if (value.typ === CoercionType.Text) {
                    textToAppend = this.escapeAppendOnSendForHtml(textToAppend);
                }
                stringToAppend += textToAppend;
            });
            if (stringToAppend.length > 0) {
                const div: HTMLElement[] = this.editor.queryElements('div#appendonsend');
                if (div.length == 0) {
                    // div is not found
                    logUsage(name, ['End', 'HTML']);
                    this.editor.insertContent(stringToAppend, {
                        position: ContentPosition.End,
                        updateCursor: false,
                        replaceSelection: false,
                        insertOnNewLine: true,
                    });
                } else {
                    for (let i = 0; i < div.length; i++) {
                        const element: HTMLElement = div[i];
                        if (element.id === 'appendonsend' && element.innerHTML === '') {
                            logUsage(name, ['Div', 'HTML']);

                            // OutlookWeb:54527
                            // tslint:disable-next-line:no-inner-html
                            element.innerHTML = stringToAppend;
                            break;
                        }
                    }
                }
            }
        } else if (bodyType == 'Text' && this.plainTextEditor) {
            appendOnSend.map(value => {
                if (value.typ !== CoercionType.Html) {
                    stringToAppend += '\n' + value.txt;
                }
            });
            if (stringToAppend.length > 0) {
                logUsage(name, ['End', 'Text']);
                const newContent = this.plainTextEditor.value + stringToAppend;
                this.plainTextEditor.value = newContent;
            }
        }
        removeAppendOnSend(viewState);
    }

    private escapeAppendOnSendForHtml(content: string) {
        content = content.replace(/&/g, '&amp;');
        content = content.replace(/</g, '&lt;');
        content = content.replace(/>/g, '&gt;');
        content = content.replace(/"/g, '&quot;');
        content = content.replace(/'/g, '&#39;');
        content = content.replace(/\//g, '&#x2F;');
        return content;
    }
}
